/* eslint-disable complexity */
import React from 'react';
import cx from 'classnames';
import { useTranslations } from '@unisporkal/localization';
import { useTheme } from '@unisporkal/themes';
import {
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import type { ImageSize, Price } from 'types/isomorphic';
import assetSizeLabelUntyped from '../../../../support/assetSizeLabel';
import PriceInfo from '../../../../components/AcquisitionForm/AssetSizes/PriceInfo/PriceInfo';
import styles from '../ModifiedBuyCard.module.scss';

// assetSizeLabel is a shared untyped (.js) helper; narrow it at the boundary.
const assetSizeLabel = assetSizeLabelUntyped as (
  t: (key: string) => string,
  size: Price
) => string;

type SizeWithJoin = Price & { modifiedSize?: ImageSize | null };

interface OriginalImageRowProps {
  size: SizeWithJoin;
  selected: boolean;
  onSelect: (size: Price) => void;
  showPrice: boolean;
  // Extend edits: the selected row also details the teeShirtSize-joined
  // modified rendition under Original/Modified headings, mirroring the legacy
  // GenImageSize presentation.
  showModifiedJoin?: boolean;
}

const OriginalImageRow = ({
  size,
  selected,
  onSelect,
  showPrice,
  showModifiedJoin = false,
}: OriginalImageRowProps) => {
  const t = useTranslations();
  const theme = (useTheme as () => { radio?: { radioButton?: string } })();
  const themeRadio = theme.radio || {};
  const label = assetSizeLabel(t, size);

  // Pixel dimensions (with locale units when available) and a dpi · megapixels
  // line, shown only when the row is selected so the collapsed row stays a
  // single label + price line. Falls back gracefully when fields are absent.
  const pixelSizes =
    size.pixelWidth && size.pixelHeight
      ? t('img_size_in_pixels', {
          width: size.pixelWidth,
          height: size.pixelHeight,
        })
      : '';
  const unitSizes =
    size.localeWidth && size.localeHeight && size.unit
      ? t('img_size_in_units', {
          width: size.localeWidth,
          height: size.localeHeight,
          units: t(`${size.unit}`),
        })
      : '';
  const dimensionLine =
    pixelSizes && unitSizes ? `${pixelSizes} (${unitSizes})` : pixelSizes;
  const megapixelSizes = size.megapixels
    ? t('img_size_in_megapixels', { size: size.megapixels })
    : '';
  const resolutionLine =
    size.dpi && megapixelSizes ? `${size.dpi} dpi | ${megapixelSizes}` : '';

  // Legacy GenImageSize file-type derivations for the Original/Modified
  // headings of the extend join view.
  const originalFileType =
    size.contentType && size.contentType !== 'JPEG'
      ? size.contentType.toUpperCase()
      : 'JPG';
  const { modifiedSize } = size;
  const modifiedFileType = modifiedSize?.fileType
    ? modifiedSize.fileType.toUpperCase()
    : 'JPG';
  const isEps = size.contentType === 'EPS';

  return (
    <div
      className={cx(styles.optionCard, {
        [styles.optionCardSelected]: selected,
      })}
      data-testid={`original-size-${size.name}`}
    >
      <label className={styles.optionHeader}>
        <input
          type="radio"
          name="original-image-size"
          className={cx(themeRadio.radioButton, styles.radio)}
          checked={selected}
          onChange={() => onSelect(size)}
        />
        <div className={styles.optionText}>
          <Typography
            component={TypographyComponentEnum.DIV}
            variant={TypographyVariant.body.medium}
            className={styles.optionLabel}
          >
            {label}
          </Typography>
          {selected && (dimensionLine || resolutionLine) && (
            <div className={styles.sizeDetails}>
              {showModifiedJoin && (
                <Typography
                  component={TypographyComponentEnum.DIV}
                  variant={TypographyVariant.body.xsmall}
                  className={styles.sizeDetailHeading}
                >
                  {t('original_image_file_type', {
                    file_type: originalFileType,
                  })}
                </Typography>
              )}
              {dimensionLine && (
                <Typography
                  component={TypographyComponentEnum.DIV}
                  variant={TypographyVariant.body.xsmall}
                  className={styles.info}
                >
                  {dimensionLine}
                </Typography>
              )}
              {resolutionLine && (
                <Typography
                  component={TypographyComponentEnum.DIV}
                  variant={TypographyVariant.body.xsmall}
                  className={styles.info}
                >
                  {resolutionLine}
                </Typography>
              )}
              {showModifiedJoin && modifiedSize && (
                <>
                  <Typography
                    component={TypographyComponentEnum.DIV}
                    variant={TypographyVariant.body.xsmall}
                    className={styles.sizeDetailHeading}
                  >
                    {t('modified_image_with_filetype', {
                      filetype: modifiedFileType,
                    })}
                  </Typography>
                  {isEps && (
                    <Typography
                      component={TypographyComponentEnum.DIV}
                      variant={TypographyVariant.body.xsmall}
                      className={styles.info}
                    >
                      {t('note_modifications_only_available_as', {
                        file_type: modifiedFileType,
                      })}
                    </Typography>
                  )}
                  <Typography
                    component={TypographyComponentEnum.DIV}
                    variant={TypographyVariant.body.xsmall}
                    className={styles.info}
                  >
                    {t('img_size_in_pixels', {
                      width: modifiedSize.width,
                      height: modifiedSize.height,
                    })}
                  </Typography>
                </>
              )}
            </div>
          )}
        </div>
        {showPrice && (
          <PriceInfo
            size={size}
            currencyCode={size.currencyCode}
          />
        )}
      </label>
    </div>
  );
};

export default OriginalImageRow;
